import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Progress, Spinner, TextInput, Textarea } from "flowbite-react";
import {
  HiOutlineBell,
  HiOutlineCursorClick,
  HiOutlineInformationCircle,
  HiOutlinePaperAirplane,
  HiOutlineUsers,
} from "react-icons/hi";
import {
  criarPushCampaign,
  enviarPushCampaign,
  getPushOverview,
} from "../../api/user";

const initialForm = {
  titulo: "",
  corpo: "",
  urlDestino: "",
};

function formatLimit(value) {
  return value === null || value === undefined ? "Ilimitado" : value;
}

function usagePercent(used, limit) {
  if (limit === null || limit === undefined || Number(limit) <= 0) return 0;
  return Math.min((Number(used || 0) / Number(limit)) * 100, 100);
}

function statusLabel(status) {
  const labels = {
    draft: "Rascunho",
    scheduled: "Agendada",
    sending: "Enviando",
    sent: "Enviada",
    failed: "Falhou",
    canceled: "Cancelada",
  };

  return labels[status] || status;
}

function UsageCard({ icon: Icon, title, used, limit }) {
  const unlimited = limit === null || limit === undefined;
  const percent = usagePercent(used, limit);

  return (
    <Card className="border border-gray-200 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-slate-100 text-slate-600">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {used}
            <span className="ml-1 text-sm font-medium text-gray-500">
              / {formatLimit(limit)}
            </span>
          </p>
          {!unlimited && <Progress progress={percent} color="purple" className="mt-3" />}
        </div>
      </div>
    </Card>
  );
}

function Notificacoes() {
  const [overview, setOverview] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const campanhas = useMemo(
    () => (Array.isArray(overview?.campanhas) ? overview.campanhas : []),
    [overview]
  );

  const carregarOverview = async () => {
    setLoading(true);
    try {
      const data = await getPushOverview();
      setOverview(data);
    } catch (error) {
      setFeedback({
        color: "failure",
        message: error.message || "Não foi possível carregar notificações.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarOverview();
  }, []);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCriarCampanha = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFeedback(null);

    try {
      await criarPushCampaign(form);
      setForm(initialForm);
      setFeedback({ color: "success", message: "Campanha criada como rascunho." });
      await carregarOverview();
    } catch (error) {
      setFeedback({
        color: "failure",
        message: error.message || "Não foi possível criar campanha.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEnviar = async (campaignId) => {
    setSendingId(campaignId);
    setFeedback(null);

    try {
      await enviarPushCampaign(campaignId);
      setFeedback({ color: "success", message: "Campanha enviada." });
      await carregarOverview();
    } catch (error) {
      setFeedback({
        color: "warning",
        message: error.message || "Não foi possível enviar campanha.",
      });
    } finally {
      setSendingId(null);
    }
  };

  if (loading && !overview) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner aria-label="Carregando notificações" size="xl" />
      </div>
    );
  }

  const bloqueado = overview && !overview.acessoPushNotifications;
  const limites = overview?.limites || {};
  const uso = overview?.uso || {};

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Notificações</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
          Envie notificações pelo navegador para visitantes inscritos da sua
          vitrine.
        </p>
      </div>

      {feedback && (
        <Alert color={feedback.color} className="mb-5">
          {feedback.message}
        </Alert>
      )}

      {bloqueado && (
        <Alert color="warning" className="mb-5">
          Notificações push não estão disponíveis no seu plano atual.
        </Alert>
      )}

      {overview?.webPushConfigurado === false && (
        <Alert color="info" className="mb-5">
          O painel já está pronto, mas o envio real depende das variáveis
          VAPID_PUBLIC_KEY e VAPID_PRIVATE_KEY no backend.
        </Alert>
      )}

      <Alert color="info" className="mb-5">
        Use notificações para novidades importantes, promoções e reposições. O
        envio confirma que a Vintree entregou a mensagem ao serviço de push do
        navegador, mas a exibição final depende das permissões do navegador e do
        sistema do visitante.
      </Alert>

      <div className="grid gap-4 md:grid-cols-3">
        <UsageCard
          icon={HiOutlineUsers}
          title="Inscritos ativos"
          used={uso.inscritosAtivos || 0}
          limit={limites.inscritos}
        />
        <UsageCard
          icon={HiOutlineBell}
          title="Notificações no mês"
          used={uso.notificacoesEnviadasMes || 0}
          limit={limites.enviosMes}
        />
        <UsageCard
          icon={HiOutlineCursorClick}
          title="Campanhas no mês"
          used={uso.campanhasEnviadasMes || 0}
          limit={limites.campanhasMes}
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <Card className="border border-gray-200 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Nova campanha</h2>
          <form className="mt-4 space-y-4" onSubmit={handleCriarCampanha}>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Título
              </label>
              <TextInput
                value={form.titulo}
                maxLength={80}
                onChange={handleChange("titulo")}
                placeholder="Novidades chegaram"
                disabled={bloqueado || saving}
              />
              <p className="mt-1 text-xs text-gray-500">{form.titulo.length}/80</p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Mensagem
              </label>
              <Textarea
                value={form.corpo}
                maxLength={180}
                rows={4}
                onChange={handleChange("corpo")}
                placeholder="Veja as peças novas da vitrine antes que acabem."
                disabled={bloqueado || saving}
              />
              <p className="mt-1 text-xs text-gray-500">{form.corpo.length}/180</p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Link de destino
              </label>
              <TextInput
                value={form.urlDestino}
                onChange={handleChange("urlDestino")}
                placeholder="https://vintree.com.br/sualoja"
                disabled={bloqueado || saving}
              />
            </div>

            <Button
              type="submit"
              color="purple"
              disabled={bloqueado || saving || !form.titulo.trim() || !form.corpo.trim()}
              className="w-full"
            >
              {saving ? <Spinner size="sm" className="mr-2" /> : <HiOutlineBell className="mr-2 h-5 w-5" />}
              Criar rascunho
            </Button>
          </form>
        </Card>

        <Card className="border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Campanhas</h2>
              <HiOutlineInformationCircle
                className="h-5 w-5 text-slate-400"
                title="Não abuse dos envios: notificações frequentes demais podem irritar visitantes e aumentar cancelamentos."
              />
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {campanhas.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                Nenhuma campanha criada ainda.
              </div>
            ) : (
              campanhas.map((campanha) => (
                <div
                  key={campanha.id}
                  className="rounded-lg border border-gray-200 bg-white p-4"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{campanha.titulo}</h3>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          {statusLabel(campanha.status)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm leading-6 text-gray-600">
                        {campanha.corpo}
                      </p>
                      <p className="mt-2 text-xs text-gray-500">
                        Alvos: {campanha.totalAlvos || 0} · Enviadas:{" "}
                        {campanha.totalEnviadas || 0} · Falhas:{" "}
                        {campanha.totalFalhas || 0}
                      </p>
                    </div>

                    <Button
                      size="xs"
                      color="dark"
                      disabled={
                        bloqueado ||
                        sendingId === campanha.id ||
                        !["draft", "failed"].includes(campanha.status)
                      }
                      onClick={() => handleEnviar(campanha.id)}
                    >
                      {sendingId === campanha.id ? (
                        <Spinner size="sm" className="mr-2" />
                      ) : (
                        <HiOutlinePaperAirplane className="mr-2 h-4 w-4" />
                      )}
                      Enviar
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Notificacoes;
